const CONSENT_VERSION = 1;
const CONSENT_KEY = "tedops_cookie_consent";
const CONSENT_ID_KEY = "tedops_consent_id";
const POSTHOG_KEY = "phc_DbsPb39SRc8z3EiQ6Dhj6ikv4H4rTKcht9d4sZSesceP";
const POSTHOG_HOST = "https://ap.emergent.sh";
const API = process.env.REACT_APP_BACKEND_URL;

export const getConsent = () => {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed.version !== CONSENT_VERSION) return null;
        return parsed;
    } catch {
        return null;
    }
};

const getConsentId = () => {
    try {
        let id = localStorage.getItem(CONSENT_ID_KEY);
        if (!id) {
            id = crypto.randomUUID();
            localStorage.setItem(CONSENT_ID_KEY, id);
        }
        return id;
    } catch {
        return "unknown";
    }
};

const recordConsent = (action, analytics) => {
    try {
        fetch(`${API}/api/consent-records`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                consent_id: getConsentId(),
                version: CONSENT_VERSION,
                analytics,
                action,
            }),
            keepalive: true,
        }).catch(() => {});
    } catch {
        /* recording is best-effort and must never block the visitor's choice */
    }
};

let posthogLoading = null;

const loadPostHog = () => {
    if (window.posthog && window.posthog.__loaded) {
        window.posthog.opt_in_capturing();
        return Promise.resolve();
    }
    if (posthogLoading) return posthogLoading;
    posthogLoading = new Promise((resolve) => {
        const s = document.createElement("script");
        s.src = `${POSTHOG_HOST}/static/array.js`;
        s.async = true;
        s.crossOrigin = "anonymous";
        s.onload = () => {
            try {
                window.posthog.init(POSTHOG_KEY, {
                    api_host: POSTHOG_HOST,
                    person_profiles: "identified_only",
                    autocapture: true,
                    disable_session_recording: true,
                });
                window.posthog.opt_in_capturing();
            } catch {
                /* never break the page over analytics */
            }
            resolve();
        };
        s.onerror = () => resolve();
        document.head.appendChild(s);
    });
    return posthogLoading;
};

const killPhStorage = () => {
    const kill = (store) => {
        try {
            const keys = [];
            for (let i = 0; i < store.length; i += 1) {
                const k = store.key(i);
                if (k && k.startsWith("ph_")) keys.push(k);
            }
            keys.forEach((k) => store.removeItem(k));
        } catch { /* noop */ }
    };
    kill(window.localStorage);
    kill(window.sessionStorage);
};

const expirePhCookies = () => {
    try {
        const hostname = window.location.hostname;
        const parts = hostname.split(".");
        const domains = [hostname, `.${hostname}`];
        if (parts.length > 2) domains.push(`.${parts.slice(-2).join(".")}`);
        document.cookie.split(";").forEach((c) => {
            const name = c.split("=")[0].trim();
            if (name.startsWith("ph_")) {
                domains.forEach((d) => {
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${d}`;
                });
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
            }
        });
    } catch { /* noop */ }
};

const deferredPurge = () => {
    const ph = window.posthog;
    if (ph && ph.__loaded) {
        try { ph.opt_out_capturing(); } catch { /* noop */ }
    }
    killPhStorage();
    expirePhCookies();
};

export const purgeOptionalTracking = () => {
    const ph = window.posthog;
    if (ph && ph.__loaded) {
        try { ph.stopSessionRecording?.(); } catch { /* noop */ }
        try { ph.opt_out_capturing(); } catch { /* noop */ }
        try { ph.reset(); } catch { /* noop */ }
    }
    killPhStorage();
    expirePhCookies();
    // PostHog re-persists fresh anonymous IDs asynchronously after reset(); purge again shortly after and on tab close.
    [600, 1800].forEach((ms) => {
        setTimeout(deferredPurge, ms);
    });
    window.addEventListener("pagehide", deferredPurge, { once: true });
};

export const saveConsent = (analytics, action) => {
    const record = {
        version: CONSENT_VERSION,
        necessary: true,
        analytics,
        action,
        ts: new Date().toISOString(),
    };
    try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    } catch { /* noop */ }
    recordConsent(action, analytics);
    if (analytics) {
        loadPostHog();
    } else {
        purgeOptionalTracking();
    }
    return record;
};

export const initConsent = () => {
    const c = getConsent();
    if (c && c.analytics) {
        loadPostHog();
    } else {
        purgeOptionalTracking();
    }
};
